import { useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, KeyRound, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import zxcvbn from "zxcvbn";
import { api } from "@/lib/api";

const CRIMSON = "#bf3b4f";
const GREEN = "#00c86e";
const MIN_SCORE = 3; // minimum password strength score
const SCORE_LABELS = ["Too guessable", "Very guessable", "Somewhat guessable", "Safely unguessable", "Very unguessable"];
const SCORE_COLORS = ["#dc2626", "#f97316", "#f59e0b", "#00c86e", "#059669"];

export function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [otp, setOtp] = useState("");
  const [challenge, setChallenge] = useState(null); // {id, otpRequired}
  const [loading, setLoading] = useState(false);

  const strength = useMemo(() => (pwd ? zxcvbn(pwd) : null), [pwd]);
  const score = strength ? strength.score : 0;
  const strongEnough = !!pwd && score >= MIN_SCORE;

  const reset = () => {
    setCurrent(""); setPwd(""); setConfirm(""); setOtp(""); setChallenge(null); setLoading(false);
  };

  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const validate = () => {
    if (!current) { toast.error("Enter your current password."); return false; }
    if (pwd.length < 8) { toast.error("New password must be at least 8 characters."); return false; }
    if (!strongEnough) { toast.error(`Please choose a stronger password (needs "${SCORE_LABELS[MIN_SCORE]}" or better).`); return false; }
    if (pwd !== confirm) { toast.error("The two new passwords don't match."); return false; }
    if (pwd === current) { toast.error("Your new password must be different from your current one."); return false; }
    return true;
  };

  const doFinish = async (challengeId) => {
    const r = await api.changePasswordFinish({
      challenge_id: challengeId, current_password: current, new_password: pwd, otp: otp || null,
    });
    // Other devices were signed out; this one continues on the fresh session.
    if (r.token) localStorage.setItem("token", r.token);
    toast.success(r.message || "Your password has been changed.");
    close(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      if (!challenge) {
        const r = await api.changePasswordStart(current);
        if (r.otp_required) {
          setChallenge({ id: r.challenge_id, otpRequired: true });
          toast.info("Enter the verification code we just sent to finish.");
        } else {
          await doFinish(r.challenge_id);
        }
      } else {
        if (!otp.trim()) { toast.error("Enter your verification code."); setLoading(false); return; }
        await doFinish(challenge.id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't change your password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" style={{ color: CRIMSON }} /> Change password
          </DialogTitle>
          <DialogDescription>
            Confirm your current password, then choose a new one for your Prydes Support account.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-semibold text-slate-700">Current password</Label>
            <Input data-testid="cp-current" type="password" value={current}
              onChange={(e) => setCurrent(e.target.value)} disabled={!!challenge}
              className="mt-1 h-11 text-sm" autoFocus required />
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700">New password</Label>
            <Input data-testid="cp-new" type="password" value={pwd}
              onChange={(e) => setPwd(e.target.value)} disabled={!!challenge}
              placeholder="At least 8 characters" className="mt-1 h-11 text-sm" required />
            {pwd && (
              <div className="mt-2" data-testid="cp-strength">
                <div className="flex gap-1">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-1.5 flex-1 rounded-full transition-colors"
                      style={{ backgroundColor: i <= score ? SCORE_COLORS[score] : "#e2e8f0" }} />
                  ))}
                </div>
                <div className="mt-1.5 flex items-center justify-between">
                  <span className="text-[11px] font-medium" style={{ color: SCORE_COLORS[score] }} data-testid="cp-strength-label">
                    {SCORE_LABELS[score]}
                  </span>
                  {strongEnough
                    ? <span className="text-[11px] font-medium flex items-center gap-1" style={{ color: GREEN }}><CheckCircle2 className="h-3 w-3" /> Strong enough</span>
                    : <span className="text-[11px] text-slate-400">Needs "{SCORE_LABELS[MIN_SCORE]}" or better</span>}
                </div>
                {!strongEnough && strength?.feedback?.warning && (
                  <p className="mt-1 text-[11px] text-amber-600">{strength.feedback.warning}</p>
                )}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700">Confirm new password</Label>
            <Input data-testid="cp-confirm" type="password" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} disabled={!!challenge}
              placeholder="Re-enter your new password" className="mt-1 h-11 text-sm" required />
            {confirm && confirm !== pwd && <p className="mt-1 text-[11px] text-rose-500">Passwords don't match.</p>}
          </div>

          {challenge?.otpRequired && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="cp-otp-step">
              <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" style={{ color: CRIMSON }} /> Verification code
              </Label>
              <p className="text-[11px] text-slate-500 mt-0.5 mb-1.5">Two-factor is on for your account — enter the code to confirm.</p>
              <Input data-testid="cp-otp" inputMode="numeric" value={otp}
                onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code"
                className="h-11 text-sm" autoFocus required />
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => close(false)} data-testid="cp-cancel" className="h-10">Cancel</Button>
            <Button data-testid="cp-submit" type="submit"
              disabled={loading || !strongEnough || pwd !== confirm}
              className="h-10 font-semibold rounded-lg hover:opacity-95 disabled:opacity-50 text-white" style={{ backgroundColor: CRIMSON }}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (challenge?.otpRequired ? "Confirm & change" : "Change password")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
